import { useState, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AlertTriangle, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { useAttentionNeeded, useDismissFlag } from '../../hooks/queries';
import { formatRelativeTime } from '../../lib/utils';
import type { CanvasserAnalyticsFilters } from '../../lib/queryKeys';
import type { AttentionNeededItem } from '@signalvote/shared';

interface AttentionNeededSectionProps {
  filters: CanvasserAnalyticsFilters;
}

function formatCategory(category: string): string {
  return category
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('-');
}

function getSeverityBorderClass(severity: AttentionNeededItem['severity']): string {
  return severity === 'critical' ? 'border-l-red-500' : 'border-l-yellow-500';
}

const CATEGORY_LABELS: Record<string, string> = {
  'no-show': 'No-Shows',
  'first-week-warning': 'First Week',
  'performance-decline': 'Performance Decline',
  'low-survey': 'Low Survey',
};

function getCategoryLabel(category: string): string {
  return CATEGORY_LABELS[category] ?? formatCategory(category);
}

export function AttentionNeededSection({ filters }: AttentionNeededSectionProps) {
  const navigate = useNavigate();
  const [showDismissed, setShowDismissed] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const { data, isLoading, isError, refetch } = useAttentionNeeded(filters, showDismissed);
  const { mutate: dismissFlag } = useDismissFlag();

  const undismissedItems = useMemo(
    () => data?.items.filter((item) => !item.isDismissed) ?? [],
    [data],
  );
  const dismissedItems = useMemo(
    () => data?.items.filter((item) => item.isDismissed) ?? [],
    [data],
  );
  const dismissedCount = data?.dismissedCount ?? 0;

  // Compute unique categories and their counts
  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of undismissedItems) {
      counts.set(item.category, (counts.get(item.category) ?? 0) + 1);
    }
    return counts;
  }, [undismissedItems]);

  const hasMultipleCategories = categories.size > 1;

  // Filter items by active category
  const visibleItems = useMemo(
    () =>
      activeCategory
        ? undismissedItems.filter((item) => item.category === activeCategory)
        : undismissedItems,
    [undismissedItems, activeCategory],
  );

  // Don't show loading state — section appears once data arrives to avoid flash
  if (isLoading) {
    return null;
  }

  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Attention Needed
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Failed to load attention needed flags.</p>
          <Button variant="outline" size="sm" className="mt-2" onClick={() => refetch()}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Hide section completely when no undismissed flags and not showing dismissed
  if (undismissedItems.length === 0 && !showDismissed) {
    return null;
  }

  const handleFlagClick = (item: AttentionNeededItem) => {
    navigate({ to: item.navigationTarget });
  };

  const handleDismiss = (e: React.MouseEvent, item: AttentionNeededItem) => {
    e.stopPropagation();
    dismissFlag({ flagKey: item.key, category: item.category });
  };

  return (
    <Card id="attention-needed-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          Attention Needed
          <Badge variant="destructive" className="ml-1">
            {undismissedItems.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Category filter chips — shown when 2+ categories */}
        {hasMultipleCategories && (
          <div className="mb-3 flex flex-wrap gap-1.5">
            <Button
              variant={activeCategory === null ? 'default' : 'outline'}
              size="sm"
              className="h-7 text-xs"
              onClick={() => setActiveCategory(null)}
            >
              All ({undismissedItems.length})
            </Button>
            {[...categories.entries()].map(([category, count]) => (
              <Button
                key={category}
                variant={activeCategory === category ? 'default' : 'outline'}
                size="sm"
                className="h-7 text-xs"
                onClick={() => setActiveCategory(category)}
              >
                {getCategoryLabel(category)} ({count})
              </Button>
            ))}
          </div>
        )}

        <div className="space-y-2">
          {visibleItems.map((item) => (
            <div
              key={item.key}
              data-flag-item
              className={`flex cursor-pointer items-center justify-between rounded-md border-l-4 p-3 transition-colors hover:bg-accent/50 ${getSeverityBorderClass(item.severity)}`}
              onClick={() => handleFlagClick(item)}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {getCategoryLabel(item.category)}
                  </Badge>
                  <span className="font-medium">{item.entityName}</span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {formatRelativeTime(item.timestamp)}
                </p>
              </div>
              {item.dismissable && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="ml-2 h-8 w-8 shrink-0"
                  aria-label={`Dismiss ${item.entityName}`}
                  onClick={(e) => handleDismiss(e, item)}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}

          {/* Dismissed items (shown when expanded) */}
          {showDismissed && dismissedItems.length > 0 && (
            <div className="mt-4 space-y-2 opacity-60">
              {dismissedItems.map((item) => (
                <div
                  key={item.key}
                  data-flag-item
                  className={`flex items-center rounded-md border-l-4 p-3 ${getSeverityBorderClass(item.severity)}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {getCategoryLabel(item.category)}
                      </Badge>
                      <span className="font-medium text-muted-foreground">{item.entityName}</span>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Dismissed count link */}
          {dismissedCount > 0 && (
            <button
              className="mt-2 text-sm text-muted-foreground underline hover:text-foreground"
              onClick={() => setShowDismissed((prev) => !prev)}
            >
              {dismissedCount} dismissed
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
